import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Modal,
  TextField,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useLazyGetBudgetCategoryQuery } from '../services/budgetCategory';
import { useAddAlertConfigMutation, useLazyGetAlertConfigByIdQuery, useUpdateAlertConfigMutation } from '../services/alertConfig';
import { useParams } from 'react-router-dom';
import { showError, showToast } from '../constants/toasts';

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>
  mode: 'add' | 'edit';
  editId?: number | string | null;
  onSuccess?: () => void;
}

export default function AddEditAlertConfig({ open, onClose, setOpen, mode, editId = null, onSuccess }: Props) {
  const title = mode === 'add' ? 'Add Alert Configuration' : 'Edit Alert Configuration';

  const [budgetCategory, setBudgetCategory] = useState<string>('');
  const [nudgeMessage, setNudgeMessage] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoadingEditData, setIsLoadingEditData] = useState<boolean>(false);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setBudgetCategory(event.target.value as string);
  };

  const [getBudgetCategory] = useLazyGetBudgetCategoryQuery();
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [addAlertConfig] = useAddAlertConfigMutation();
  const [updateAlertConfig] = useUpdateAlertConfigMutation();
  const [getById] = useLazyGetAlertConfigByIdQuery();

  const getBudgetCatgeoryList = async () => {
    setIsLoading(true);
    try {
      const response = await getBudgetCategory({
        search: "",
        start: page,
        length: 1000
      }).unwrap();
      if (response?.status === 200) {
        setData(response?.data?.response_object)
      } else {
        setData([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEditData = async (id: number | string) => {
    setIsLoadingEditData(true);
    try {
      const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
      if (!idNum || Number.isNaN(idNum)) return;
      
      const response = await getById({ id: idNum }).unwrap();
      if (response?.status === 200 && response?.data) {
        const alertData = response.data;
        setBudgetCategory(alertData.budget_category?.toString() || '');
        setNudgeMessage(alertData.nudge_message || '');
      }
    } catch (error: any) {
      showError(error?.data?.message || 'Failed to load alert configuration');
      console.log(error);
    } finally {
      setIsLoadingEditData(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (open) {
        setError(false); // Reset error state when modal opens
        await getBudgetCatgeoryList();
        if (mode === 'edit' && editId) {
          await loadEditData(editId);
        } else if (mode === 'add') {
          setBudgetCategory('');
          setNudgeMessage('');
        }
      }
    };
    init();
  }, [open, mode, editId]);

  const handleSubmit = async () => {
    setError(true);
    const trimmedMessage = nudgeMessage?.trim() ?? '';
    
    if (!budgetCategory) {
      showError('Please select a budget category');
      return;
    }
    
    if (!trimmedMessage.length) {
      showError('Please enter nudge message');
      return;
    }

    setIsSubmitting(true);
    try {
      const body = {
        budget_category: parseInt(budgetCategory),
        nudge_message: trimmedMessage
      };

      let response: any;
      if (mode === 'edit' && editId) {
        const idNum = typeof editId === 'string' ? parseInt(editId, 10) : editId;
        response = await updateAlertConfig({ body, id: idNum as number }).unwrap();
      } else {
        response = await addAlertConfig({ body }).unwrap();
      }
      if (response?.status === 200 || response?.status === 201 || response?.statusCode === 200) {
        setBudgetCategory('');
        setNudgeMessage('');
        setOpen(false);
        onSuccess?.();
        showToast( (mode === 'edit' ? 'Alert configuration updated successfully' : 'Alert configuration added successfully'));
      }
    } catch (error: any) {
      showError(error?.data?.message || 'Something went wrong');
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Modal
      className="modal alertConfig_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{title}</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" action="">
            <div className="control_group">
              <label>Select Budget Category</label>
              <Select
                fullWidth
                value={budgetCategory}
                onChange={handleCategoryChange}
                disabled={isLoading || isLoadingEditData}
                displayEmpty
              >
                <MenuItem disabled value="">
                  {isLoading ? 'Loading categories...' : isLoadingEditData ? 'Loading data...' : 'Select a budget category...'}
                </MenuItem>
                {data.map((category) => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.category_name}
                  </MenuItem>
                ))}
              </Select>
              {error && !budgetCategory ? (
                <h6 className="err_msg">Budget category is required</h6>
              ) : (
                ''
              )}
            </div>
            <div className="control_group">
              <label>Customize Nudge Message</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter Customize Nudge Message"
                multiline
                rows={2}
                value={nudgeMessage}
                onChange={(e) => setNudgeMessage(e.target.value)}
                disabled={isLoadingEditData}
              />
              {error && !nudgeMessage.trim() ? (
                <h6 className="err_msg">Nudge message is required</h6>
              ) : (
                ''
              )}
            </div>
            <div className="footer_btn_flex">
              <Button 
                className="btn btn_grey" 
                onClick={() => {
                  setError(false);
                  setOpen(false);
                  setBudgetCategory('');
                  setNudgeMessage('');
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                className="btn btn_primary" 
                onClick={handleSubmit}
                disabled={isSubmitting || isLoading || isLoadingEditData}
              >
                {isSubmitting ? 'Processing...' : isLoadingEditData ? 'Loading...' : mode === 'edit' ? 'Update' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
